import React from "react"

import { useActiveOrganization } from "@/contexts/active-organization-context"
import type { Currency } from "@/models/currency"
import type { CurrencyCode } from "@/models/currencyCode"
import type { SupportedMeasurementSystemCode } from "@/models/measurementSystem"
import { useCurrencies } from "@/services/currency"
import { DEFAULT_CURRENCY } from "@/utils/constants"
import { getDefaultCurrencyCode } from "@/utils/currency"
import { isSupportedMeasurementSystemCode } from "@/utils/measurement-system"
import { useSaveAppState } from "@/utils/use-save-app-state"

const currenciesToCurrencyCodes = (
  currencies: Currency[] | undefined
): CurrencyCode[] => currencies?.map((currency) => currency.alphaCode) ?? []

export interface UseUserSettingsValue {
  activeMeasurementSystem: SupportedMeasurementSystemCode
  currency: CurrencyCode
  setActiveMeasurementSystem: (
    measurementSystemCode: SupportedMeasurementSystemCode
  ) => void
  supportedCurrencies: CurrencyCode[]
  supportedCurrenciesIsFetched: boolean
  unitsSystem: string
}

export interface UserSettingsProviderProps extends Partial<UseUserSettingsValue> {
  children: React.ReactNode
}

const UserSettingsContext = React.createContext<
  UseUserSettingsValue | undefined
>(undefined)

/**
 * Provides the user settings context
 *
 * @example
 * return (
 *   <UserSettingsProvider>
 *     <Component />
 *   </UserSettingsProvider>
 * )
 */
export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = (
  props
) => {
  const { saveMeasurementSystem, savedMeasurementSystem } = useSaveAppState()

  const { activeOrganization } = useActiveOrganization()
  const orgLocale = activeOrganization?.locale
  const { currenciesData, currenciesIsFetched } = useCurrencies()

  // Convert currencies to currency codes and filter for supported ones
  const supportedCurrencies = React.useMemo(() => {
    return (
      props.supportedCurrencies ?? currenciesToCurrencyCodes(currenciesData)
    )
  }, [currenciesData, props.supportedCurrencies])

  const activeCurrency = React.useMemo(() => {
    return orgLocale ? getDefaultCurrencyCode(orgLocale) : DEFAULT_CURRENCY
  }, [orgLocale])

  const activeMeasurementSystem =
    props.activeMeasurementSystem ?? savedMeasurementSystem

  const unitsSystem = React.useMemo(() => {
    return orgLocale ?? "en-US"
  }, [orgLocale])

  // Function to set measurement system (saves to localStorage and updates state)
  const setActiveMeasurementSystemPublic = React.useCallback(
    (measurementSystemCode: SupportedMeasurementSystemCode) => {
      const validMeasurementSystemCode = isSupportedMeasurementSystemCode(
        measurementSystemCode
      )
        ? measurementSystemCode
        : savedMeasurementSystem

      if (props.setActiveMeasurementSystem) {
        props.setActiveMeasurementSystem(validMeasurementSystemCode)
      } else {
        saveMeasurementSystem(validMeasurementSystemCode)
      }
    },
    [props, saveMeasurementSystem, savedMeasurementSystem]
  )

  const value: UseUserSettingsValue = React.useMemo(
    () => ({
      currency: activeCurrency,
      activeMeasurementSystem,
      setActiveMeasurementSystem: setActiveMeasurementSystemPublic,
      supportedCurrencies,
      supportedCurrenciesIsFetched:
        props.supportedCurrenciesIsFetched ?? currenciesIsFetched,
      unitsSystem,
    }),
    [
      activeCurrency,
      activeMeasurementSystem,
      currenciesIsFetched,
      props.supportedCurrenciesIsFetched,
      setActiveMeasurementSystemPublic,
      supportedCurrencies,
      unitsSystem,
    ]
  )

  return (
    <UserSettingsContext value={value}>{props.children}</UserSettingsContext>
  )
}

/**
 * Provides user settings context
 *
 * @example
 * const { currency, setCurrency } = useUserSettings()
 */
export const useUserSettings = (isDisabled?: boolean): UseUserSettingsValue => {
  const context = React.use(UserSettingsContext)

  // Needed to ignore currency change in some React Query keys
  if (isDisabled) {
    return {
      activeMeasurementSystem: "USCS",
      currency: DEFAULT_CURRENCY,
      setActiveMeasurementSystem: () => {
        console.warn("useUserSettings is disabled")
      },
      supportedCurrencies: [],
      supportedCurrenciesIsFetched: false,
      unitsSystem: "en-US",
    }
  }

  if (context === undefined) {
    throw new Error(
      "useUserSettings must be used within a UserSettingsProvider"
    )
  }

  return context
}
